<template>
	 <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('lifeCycleCreate') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId" >
                        {{ assetTypeName || $t('assetType') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ $t('lifeCycleCreate') }}</li>
            </ol>
        </nav>
        <div class="row mb-4">
            <div class="col-md-4">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
              <div class="col-md-4">
                <label class="form-label" for="performanceId" required>{{ $t('performance') }}</label>
                <select class="form-select" v-model="data.performanceId">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="performanceList.length > 0">
                        <option v-for="(performance, i) in performanceList" :key="i"  v-bind:value="performance.id">{{ performance.name }}</option>
                    </template>
                </select>
            </div>
              <div class="col-md-4">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description">
            </div>
        </div>
        <div class="row mb-4">
            <div class="col-md-4">
                <label class="form-label" for="mandatoryActAttr" required>{{ $t('mandatoryActAttr') }}</label>
                <select id="mandatoryActAttr" class="form-select" v-model="data.mandatoryActAttr">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i"  v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
            <div class="col-md-4">
                <label class="form-label" for="mandatoryActYearAttr" required>{{ $t('mandatoryActYearAttr') }}</label>
                <select id="mandatoryActYearAttr" class="form-select" v-model="data.mandatoryActYearAttr">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i"  v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
            <div class="col-md-4">
                <label class="form-label" for="mandatoryActCostAttr" required>{{ $t('mandatoryActCostAttr') }}</label>
                <select id="mandatoryActCostAttr" class="form-select" v-model="data.mandatoryActCostAttr">
                    <option value=""></option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
        </div>
        <div class="mb-4">
            <h4>{{ $t('lifeCycleActivities') }}</h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col" class="col-2">{{ $t('activity') }}</th>
                        <th scope="col">{{ $t('minAge') }}</th>
                        <th scope="col">{{ $t('maxAge') }}</th>
                        <th scope="col">{{ $t('noAct') }}</th>
                        <th scope="col">{{ $t('signif') }}</th>
                        <th scope="col">{{ $t('effect') }}</th>
                        <th scope="col">{{ $t('notFirst') }}</th>
                        <th scope="col">{{ $t('changeToLifeCycle') }}</th>
                        <th scope="col"></th>
                        <th scope="col"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(lifeCycleActivity, i) in data.lifeCycleActivities" class="align-middle" :key="i" >
                        <th scope="row"><i class="fa fa-remove" @click="removeLifeCycleActivity(i)"></i></th>
                        <td>
                            <select :id="'activityId_' + i" class="form-select" v-model="lifeCycleActivity.activityId" >
                                <option disabled>{{ $t('select') }}</option>
                                <template v-if="activityList.length > 0" >
                                    <option v-for="(activity, i) in activityList" :key="i" v-bind:value="activity.id">{{ getActivityName(activity) }}</option>
                                </template>
                            </select>
                        </td>
                        <td>                    
                            <input v-model="lifeCycleActivity.minAge" type="number" class="form-control" :id="'minAge_' + i">
                        </td>
                        <td>
                            <input v-model="lifeCycleActivity.maxAge" type="number" class="form-control" :id="'maxAge_' + i">
                        </td>
                        <td>
                            <input v-model="lifeCycleActivity.noAct" type="number" class="form-control" :id="'noAct_' + i">
                        </td>
                        <td>
                            <input v-model="lifeCycleActivity.significant" type="number" class="form-control" :id="'significant_' + i">
                        </td>
                        <td>
                            <div class="input-group">
                                <input v-model="lifeCycleActivity.effect" type="number" class="form-control" :id="'effect_' + i">
                                <select :id="'effecttype_' + i" class="form-select" v-model="lifeCycleActivity.effectType" >
                                    <option value="0">{{ $t('Absolute') }}</option>
                                    <option value="1">{{ $t('Relative') }}</option>
                                    <option value="2">{{ $t('Differential') }}</option>
                                </select>
                            </div>
                        </td>
                        <td>
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="lifeCycleActivity.notFirst" :id="'notFirst_' + i">
                            </div>
                        </td>
                        <td>
                            <select :id="'changeToLifecycleId_' + i" class="form-select" v-model="lifeCycleActivity.changeToLifecycleId" >
                                <option selected :value=0></option>
                                <template v-if="lifeCycleList.length > 0" >
                                    <option v-for="(lifeCycle, o) in lifeCycleList" :key="o" v-bind:value="lifeCycle.id">{{ lifeCycle.name }}</option>
                                </template>
                            </select>
                        </td>
                        <td><i v-if='i < data.lifeCycleActivities.length-1' class="fa fa-arrow-down" @click="moveActivityDown(i)"/></td>
                        <td><i v-if='i != 0' class="fa fa-arrow-up" @click="moveActivityUp(i)" /></td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block" @click="addLifeCycleActivity"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div class="mb-3">
            <h4 class="mb-3">{{ $t('lifeCycleFilters') }} </h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('name') }}</th>
                        <th scope="col">{{ $t('sign') }}</th>
                        <th scope="col">{{ $t('number') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(lifeCycleFilter, i) in data.lifeCycleFilters" class="align-middle"  :key="i" >
                        <th scope="row"><i class="fa fa-remove" @click="removeLifeCycleFilter(i)"></i></th>
                        <td>
                            <select :id="'name_' + i" class="form-select" v-model="lifeCycleFilter.name">
                                <option disabled>{{ $t('select') }}</option>
                                <template v-if="datasetHeaders.length > 0" >
                                    <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                                </template>
                            </select>
                        </td>
                        <td>
                            <select v-model="lifeCycleFilter.sign" class="form-select"  :id="'sign_' + i"> 
                                <option value="=">=</option>
                                <option value=">">&gt;</option>
                                <option value="<">&lt;</option>
                                <option value=">=">&gt;=</option>
                                <option value="<=">&lt;=</option>
                            </select>
                        </td>
                        <td>
                            <input v-model="lifeCycleFilter.number" type="number" class="form-control" :id="'number_' + i">
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block" @click="addLifeCycleFilter"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div class="d-grid gap-2 d-md-flex ">
            <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('create') }}</button>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })">{{ $t('back') }}</button>
        </div>
    </div>
</template>

<script>
import LifeCycleApi from "../../api/server/lifeCycle";
import PerformanceApi from "../../api/server/performance";
import ActivityApi from "../../api/server/activity";
import DatasetApi from "../../api/server/dataset";
import ModelingApi from "../../api/server/modeling";
import AssetTypeApi from "../../api/server/assetType";

export default {
    name: "lifeCycleCreate",
    watch: {
        $route: "requestData"
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.pending = true;

            var assetType = await AssetTypeApi.assetTypeDetail(this.$route.params.id, this.$route.params.assetTypeId);
            this.assetTypeName = assetType.name;

            var dataset = await DatasetApi.datasetDetail(this.$route.params.id, assetType.datasetId);

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            var lifeCycleList = await LifeCycleApi.lifeCycleList(this.$route.params.id);
            this.lifeCycleList = lifeCycleList.filter(l => l.assetTypeId == this.$route.params.assetTypeId);

            var datasetContent = JSON.parse(dataset.content);
            this.datasetHeaders = datasetContent["headers"];

            var performanceList = await PerformanceApi.performanceList(this.$route.params.id);
            this.performanceList = performanceList.filter(p => p.assetTypeId == this.$route.params.assetTypeId);    

            var activityList = await ActivityApi.activityList(this.$route.params.id);
            this.activityList = activityList.filter(a => a.assetTypeId == this.$route.params.assetTypeId);

            this.pending = false;
        },
        async onSubmit() {
            if(this.data.name.length > 0) {
                if(this.data.performanceId > 0) {
                    if(!this.areLifeCycleActivitiesValid()) {
                        this.showActivitiesError();
                    } else if (!this.areLifeCycleFilterNumberAndSignValid()) {
                        this.showFiltersError();
                    } else {
                        if(!this.isFormValid()) {
                            this.showWarning();
                        } else {
                            this.data.assetTypeId = this.$route.params.assetTypeId;
                            await LifeCycleApi.lifeCycleCreate(this.$route.params.id, this.data);
                            this.$router.go(-1);
                        }
                    }
                }
                else {
                    this.showMissingPerformanceWarning();
                }
            } else {
                this.showEmptyNameError();
            }
        },
        areLifeCycleFilterNumberAndSignValid() {
            for(var i in this.data.lifeCycleFilters) {
                var attr = this.data.lifeCycleFilters[i];
                if(attr.number === "") 
                    return false;
                if(attr.sign === "") 
                    return false;
            }

            return true
        },
        areLifeCycleActivitiesValid() {
            for(var i in this.data.lifeCycleActivities) {
                var attr = this.data.lifeCycleActivities[i];
                if(attr.minAge === "") 
                    return false;
                if(attr.maxAge === "") 
                    return false;
                if(attr.noAct === "") 
                    return false;
                if(attr.significant === "") 
                    return false;
                if(attr.effect === "") 
                    return false;
                if(attr.activityId == 0)
                    return false;
            }

            return true
        },
        isFormValid() {
            var mandatoryActAttr = this.data.mandatoryActAttr;

            if(this.datasetHeaders.findIndex(d => d.name == mandatoryActAttr) < 0 && mandatoryActAttr != "")
                return false;

            var mandatoryActYearAttr = this.data.mandatoryActYearAttr;
            if(this.datasetHeaders.findIndex(d => d.name == mandatoryActYearAttr) < 0 && mandatoryActYearAttr != "")
                return false;

            return true;
        },
        addLifeCycleActivity() {
            this.data.lifeCycleActivities.push({  
                activityId: 0,
                activityName: "",
                activityUnitCost: 0,
                activityCostFactor:"",
                minAge: this.getMinAge(),
                maxAge: 0,
                noAct: 0,
                significant: 0,
                effect: 0,
                effectType: 0,
                notFirst: false,
                changeToLifecycleId: 0,
                root: false,
                order: this.getActivityOrder()
            })
        },
        getMinAge() {
            var minAge = 0;
            if(this.data.lifeCycleActivities.length > 0)
                minAge = this.data.lifeCycleActivities[this.data.lifeCycleActivities.length -1].maxAge;

            return minAge;
        },
        getActivityOrder() {
            var order = 0;
            if(this.data.lifeCycleActivities.length > 0)
                order = this.data.lifeCycleActivities[this.data.lifeCycleActivities.length -1].order + 1;

            return order;
        },
        removeLifeCycleActivity(rowIndex) {
            this.data.lifeCycleActivities.splice(rowIndex, 1);
        },
        removeLifeCycleFilter(rowIndex) {
            this.data.lifeCycleFilters.splice(rowIndex, 1);
        },
        addLifeCycleFilter() {
            this.data.lifeCycleFilters.push({  
                name: "",
                sign: "=",
                number: 0
            })
        },
        getActivityName(activity) {
            return `${activity.name} ${activity.rootActivity ? " (root)" : ""}`;
        },
        moveActivityUp(i) {
            var lifeCycleActivities = this.data.lifeCycleActivities;
            if (i > 0) {
                let indexOfActivityUp = i - 1;
                let tmpOrder = lifeCycleActivities[indexOfActivityUp].order;
                lifeCycleActivities[indexOfActivityUp].order = lifeCycleActivities[i].order;
                lifeCycleActivities[i].order = tmpOrder;

                lifeCycleActivities.sort((a, b) => a.order - b.order);

                this.data.lifeCycleActivities = lifeCycleActivities;
            }
        },
        moveActivityDown(i) {
            var lifeCycleActivities = this.data.lifeCycleActivities;
            if (i >= 0) {
                let indexOfActivityDown = i + 1;
                let tmpOrder = lifeCycleActivities[indexOfActivityDown].order;
                lifeCycleActivities[indexOfActivityDown].order = lifeCycleActivities[i].order;
                lifeCycleActivities[i].order = tmpOrder;

                lifeCycleActivities.sort((a, b) => a.order - b.order);

                this.data.lifeCycleActivities = lifeCycleActivities;
            }
        },
        orderedLifeCycleActivities(lifeCycleActivities) {
            return lifeCycleActivities.sort((a, b) => a.order - b.order);
        }
    },
    notifications: {
        showMissingPerformanceWarning: { 
            title: 'error',
            message: 'missingPerformanceWarning',
            type: 'error'
        },
        showFiltersWarning: { 
            title: 'warning',
            message: 'filtersWarning',
            type: 'warn'
        },
        showFiltersError: {
            title: 'error',
            message: 'filtersError',
            type: 'error'
        },
        showActivitiesError: { 
            title: 'error',
            message: 'activitiesError',
            type: 'error'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            datasetHeaders: [],
            data: {
                id: 0,
                name: "",
                description: "",
                performanceId: 0,
                assetTypeId: 0,
                mandatoryActAttr: "",
                mandatoryActYearAttr: "",
                mandatoryActCostAttr: "",
                lifeCycleActivities: [],
                lifeCycleFilters: []
            },
            lifeCycleList: [],
            performanceList: [],
            activityList: [],
            modelingName: null,
            assetTypeName: null,
            pending: true
        }
    }
};
</script>